import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Mail, Lock, User, Eye, EyeOff, KeyRound, Crown, Zap, Building2, ArrowRight, Shield, BarChart3, Users, Star, CheckCircle, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getRedirectPath = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
    if (!res.ok) return '/dashboard';
    const user = await res.json();
    const role = user.account_role || user.role;
    return role === 'comisionista' ? '/portal-comisionista' : '/dashboard';
  } catch { return '/dashboard'; }
};

const heroImages = [
  'https://images.unsplash.com/photo-1665224715192-112f4623b665?w=800&q=80',
  'https://images.unsplash.com/photo-1650057973182-a38579f37393?w=800&q=80',
  'https://images.unsplash.com/photo-1678376985186-9a47f3e7d67f?w=800&q=80',
  'https://images.unsplash.com/photo-1578059873395-a99958e1e305?w=800&q=80'
];

const plans = [
  {
    id: 'control',
    name: 'Control',
    description: 'Operación organizada',
    icon: Building2,
    color: 'border-zinc-700',
    monthly: 49,
    annual: 470,
    features: ['1 fraccionamiento', '3 usuarios', 'Dashboard financiero', 'Estado de cuenta', 'Soporte estándar']
  },
  {
    id: 'profesional',
    name: 'Profesional',
    description: 'Control financiero avanzado',
    icon: Zap,
    color: 'border-[#C6A54A]',
    popular: true,
    monthly: 89,
    annual: 854,
    features: ['3 fraccionamientos', '5 usuarios', 'KPIs financieros', 'Notificaciones', 'Comisiones', 'Soporte prioritario']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Plataforma corporativa',
    icon: Crown,
    color: 'border-purple-500',
    monthly: 149,
    annual: 1430,
    features: ['Ilimitados', '10 usuarios', 'Auditoría completa', 'Branding', 'Onboarding', 'Soporte 24/7']
  }
];

const stats = [
  { value: '150+', label: 'Desarrolladores' },
  { value: '$2.5B', label: 'MXN Gestionados' },
  { value: '12,000+', label: 'Lotes Vendidos' },
  { value: '98%', label: 'Satisfacción' }
];

const regions = ['Nuevo León', 'Jalisco', 'CDMX', 'Querétaro', 'Yucatán', 'Sinaloa'];

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });

  // Plan selection from /precios page
  const selectedPlan = searchParams.get('plan');
  const selectedCycle = searchParams.get('cycle');

  // Comisionista login state
  const [comisionistaForm, setComisionistaForm] = useState({ email: '', password: '' });
  const [comisionistaLoading, setComisionistaLoading] = useState(false);
  const [showComisionistaPassword, setShowComisionistaPassword] = useState(false);

  // Recuperación de contraseña
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  // Invitaciones
  const [invitationToken, setInvitationToken] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [invitationError, setInvitationError] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);

  useEffect(() => {
    const token = searchParams.get('invitation');
    if (token) {
      setInvitationToken(token);
      setAuthMode('invitation');
      verifyInvitation(token);
    }
    // If coming from pricing page with a plan, show register mode
    if (searchParams.get('plan')) {
      setAuthMode('register');
      // Auto-scroll to auth section
      setTimeout(() => {
        document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [searchParams]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const path = await getRedirectPath();
          navigate(path);
        }
      } catch {}
    };
    if (!invitationToken) checkAuth();
  }, [navigate, invitationToken]);

  const verifyInvitation = async (token) => {
    setLoadingInvitation(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/invitation/${token}`);
      const text = await response.text();
      let data = {};
      try { if (text) data = JSON.parse(text); } catch {}
      if (response.ok) {
        setInvitationData(data);
        setFormData(prev => ({ ...prev, email: data.invitation?.email || data.email || '' }));
      } else {
        setInvitationError(data.detail || 'Invitación inválida');
      }
    } catch {
      setInvitationError('Error al verificar');
    } finally {
      setLoadingInvitation(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Ingresa tu correo'); return; }
    setSendingReset(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      toast.success('Si el correo existe, recibirás instrucciones');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch { toast.error('Error al enviar'); }
    finally { setSendingReset(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/register';
    const body = authMode === 'login'
      ? { email: formData.email, password: formData.password }
      : { email: formData.email, password: formData.password, name: formData.name };

    if (authMode === 'register' && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      let data = {};
      try { data = await response.json(); } catch {}

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(data.detail || 'Demasiados intentos. Espera unos minutos.');
        } else if (response.status === 401) {
          throw new Error('Correo o contraseña incorrectos');
        } else {
          throw new Error(data.detail || 'Error al procesar solicitud');
        }
      }

      toast.success('¡Bienvenido!');
      
      // If a plan was selected from /precios, go to Stripe checkout
      if (selectedPlan) {
        try {
          const checkoutRes = await fetch(`${BACKEND_URL}/api/subscription/create-checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ 
              origin_url: window.location.origin, 
              plan: selectedPlan, 
              billing_cycle: selectedCycle === 'annual' ? 'yearly' : (selectedCycle || 'monthly') 
            })
          });
          if (checkoutRes.ok) {
            const checkoutData = await checkoutRes.json();
            window.location.href = checkoutData.checkout_url;
            return;
          }
        } catch {}
      }
      
      const path = await getRedirectPath();
      navigate(path);
    } catch (error) {
      toast.error(error.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Ingresa tu nombre'); return; }
    if (formData.password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/team/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: invitationToken, name: formData.name, password: formData.password })
      });
      let data = {};
      try { data = await response.json(); } catch {}
      if (!response.ok) throw new Error(data.detail || 'Error');
      toast.success('¡Bienvenido al equipo!');
      const path = await getRedirectPath();
      navigate(path);
    } catch (error) {
      toast.error(error.message || 'Error al aceptar');
    } finally { setLoading(false); }
  };

  const getPrice = (plan) => billingCycle === 'annual' ? plan.annual : plan.monthly;
  const getSavings = (plan) => (plan.monthly * 12) - plan.annual;

  const handleComisionistaLogin = async (e) => {
    e.preventDefault();
    setComisionistaLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: comisionistaForm.email, password: comisionistaForm.password })
      });
      let data = {};
      try { data = await response.json(); } catch {}
      if (!response.ok) {
        if (response.status === 429) throw new Error(data.detail || 'Demasiados intentos. Espera unos minutos.');
        if (response.status === 401) throw new Error('Correo o contraseña incorrectos');
        throw new Error(data.detail || 'Error al iniciar sesión');
      }
      const role = data.account_role || data.role;
      if (role !== 'comisionista') {
        toast.error('Este acceso es exclusivo para comisionistas. Usa el formulario principal.');
        return;
      }
      toast.success('¡Bienvenido, Comisionista!');
      navigate('/portal-comisionista');
    } catch (error) {
      toast.error(error.message || 'Error de conexión');
    } finally {
      setComisionistaLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-y-auto">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png"
            alt="Ibañez" className="h-14 sm:h-16"
          />
          <div className="flex items-center gap-3">
            <a href="#planes" className="text-zinc-400 hover:text-white text-sm hidden sm:block">Ver Planes</a>
            <Button
              onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold"
              size="sm"
            >
              Comenzar Gratis
            </Button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="pt-28 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 h-full opacity-20">
            {heroImages.map((img, i) => (
              <div key={i} className="overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <img
            src="https://customer-assets.emergentagent.com/job_lotesys/artifacts/fyrjj46v_Logo%20IBA%C3%91EZ%20para%20desarrollos%20inmobiliarios.png"
            alt="Ibañez Desarrollos"
            className="h-32 sm:h-40 lg:h-48 mx-auto mb-8"
          />

          <Badge className="bg-[#C6A54A]/20 text-[#D4B85A] mb-6">
            #1 en Gestión Inmobiliaria en México
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            El Sistema que usan los<br />
            <span className="text-[#C6A54A]">Desarrolladores más Exitosos</span>
          </h1>

          <p className="text-xl text-zinc-400 max-w-3xl mx-auto mb-10">
            Control financiero, transparencia total y auditoría completa para tu desarrollo inmobiliario.
            Únete a más de 150 desarrolladores que ya confían en nosotros.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {stats.map((stat, i) => (
              <div key={i} className="text-center bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
                <p className="text-3xl sm:text-4xl font-bold text-[#C6A54A]">{stat.value}</p>
                <p className="text-zinc-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 text-zinc-600">
            <span className="text-sm">Presencia en:</span>
            {regions.map((region, i) => (
              <span key={i} className="px-3 py-1 bg-zinc-900 rounded-full text-xs text-zinc-400 border border-zinc-800">
                {region}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PLANES SECTION */}
      <section id="planes" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Planes para cada etapa de tu negocio
            </h2>
            <p className="text-zinc-400 mb-8">
              Elige el plan que mejor se adapte a tu operación
            </p>

            <div className="inline-flex bg-zinc-900 p-1 rounded-xl border border-zinc-800">
              <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 rounded-lg font-medium transition ${billingCycle === 'monthly' ? 'bg-[#C6A54A] text-black' : 'text-zinc-400'}`}>
                Mensual
              </button>
              <button onClick={() => setBillingCycle('annual')} className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${billingCycle === 'annual' ? 'bg-[#C6A54A] text-black' : 'text-zinc-400'}`}>
                Anual <Badge className="bg-green-500 text-white text-xs">-20%</Badge>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const Icon = plan.icon;
              return (
                <div key={plan.id} className={`relative rounded-2xl border-2 ${plan.color} bg-zinc-900 p-6 ${plan.popular ? 'ring-2 ring-[#C6A54A]/50 scale-105 z-10' : ''}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#C6A54A] text-black font-bold px-4">RECOMENDADO</Badge>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${plan.popular ? 'bg-[#C6A54A]/20' : 'bg-zinc-800'}`}>
                      <Icon className={`w-6 h-6 ${plan.popular ? 'text-[#C6A54A]' : 'text-zinc-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-zinc-500 text-sm">{plan.description}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">${getPrice(plan)}</span>
                    <span className="text-zinc-500 ml-1">USD/{billingCycle === 'annual' ? 'año' : 'mes'}</span>
                    {billingCycle === 'annual' && (
                      <p className="text-green-500 text-sm mt-1">Ahorras ${getSavings(plan)} USD</p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                        <CheckCircle className={`w-4 h-4 ${plan.popular ? 'text-[#C6A54A]' : 'text-green-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`w-full ${plan.popular ? 'bg-[#C6A54A] hover:bg-[#D4B85A] text-black' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}
                  >
                    Comenzar Ahora <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-zinc-600 text-sm mt-8">
            Sin contratos · Cancela cuando quieras · Datos 100% seguros
          </p>
        </div>
      </section>

      {/* AUTH SECTION */}
      <section id="auth-section" className="py-16 px-4 relative min-h-[600px]">
        <div className="absolute inset-0 z-0">
          <div className="grid grid-cols-2 h-full">
            <div className="relative overflow-hidden">
              <img src={heroImages[0]} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
            </div>
            <div className="relative overflow-hidden">
              <img src={heroImages[1]} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-black via-black/80 to-transparent"></div>
            </div>
          </div>
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="max-w-md mx-auto relative z-10">
          <div className="bg-zinc-900/95 backdrop-blur-xl rounded-2xl p-8 border border-zinc-700 shadow-2xl">
            {authMode === 'invitation' ? (
              loadingInvitation ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-[#C6A54A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-zinc-400">Verificando invitación...</p>
                </div>
              ) : invitationError ? (
                <div className="text-center py-8">
                  <h2 className="text-xl font-bold text-white mb-2">Invitación Inválida</h2>
                  <p className="text-zinc-400 mb-4">{invitationError}</p>
                  <Button onClick={() => { setAuthMode('login'); setInvitationToken(null); }} className="bg-[#C6A54A] text-black">
                    Iniciar Sesión
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleAcceptInvitation} className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-bold text-white">¡Únete al Equipo!</h2>
                    <p className="text-zinc-400 text-sm">Invitado como {invitationData?.role}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Nombre</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="Tu nombre" className="bg-zinc-800 border-zinc-700 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Contraseña</Label>
                    <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required placeholder="Mínimo 6 caracteres" className="bg-zinc-800 border-zinc-700 text-white mt-1" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold">
                    {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : 'Unirme'}
                  </Button>
                </form>
              )
            ) : (
              <>
                {/* Banner when coming from pricing with plan selection */}
                {selectedPlan && (
                  <div className="mb-4 p-3 bg-[#C6A54A]/10 border border-[#C6A54A]/30 rounded-lg text-center">
                    <p className="text-[#C6A54A] text-sm font-medium">
                      Plan {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} · {selectedCycle === 'annual' ? 'Anual' : 'Mensual'}
                    </p>
                    <p className="text-zinc-400 text-xs mt-1">
                      {authMode === 'register' ? 'Crea tu cuenta para continuar al pago' : 'Inicia sesión para continuar al pago'}
                    </p>
                  </div>
                )}

                {/* ===== SECCIÓN ADMINISTRADOR ===== */}
                <div className="flex gap-2 mb-6" data-testid="admin-auth-toggle">
                  <button
                    onClick={() => setAuthMode('login')}
                    data-testid="admin-tab-login"
                    className={`flex-1 py-2 rounded-lg font-medium transition ${authMode === 'login' ? 'bg-[#C6A54A] text-black' : 'bg-zinc-800 text-zinc-400'}`}
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => setAuthMode('register')}
                    data-testid="admin-tab-register"
                    className={`flex-1 py-2 rounded-lg font-medium transition ${authMode === 'register' ? 'bg-[#C6A54A] text-black' : 'bg-zinc-800 text-zinc-400'}`}
                  >
                    Crear Cuenta
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
                  {authMode === 'register' && (
                    <div>
                      <Label className="text-zinc-400">Nombre</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="Tu nombre" className="pl-10 bg-zinc-800 border-zinc-700 text-white" data-testid="admin-register-name" />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-zinc-400">Correo electrónico</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required placeholder="tu@correo.com" className="pl-10 bg-zinc-800 border-zinc-700 text-white" data-testid="login-email" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-400">Contraseña</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required placeholder="••••••••" className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white" data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-11 bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold" data-testid="login-submit">
                    {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : authMode === 'login' ? (selectedPlan ? 'Entrar y Continuar al Pago' : 'Entrar') : (selectedPlan ? 'Crear Cuenta y Pagar' : 'Crear Cuenta Gratis')}
                  </Button>

                  {authMode === 'login' && (
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="w-full text-[#C6A54A] hover:text-[#D4B85A] text-sm" data-testid="forgot-password-link">
                      ¿Olvidaste tu contraseña?
                    </button>
                  )}
                </form>

                <div className="flex items-center my-6">
                  <div className="flex-1 border-t border-zinc-700"></div>
                  <span className="px-3 text-xs text-zinc-500">o</span>
                  <div className="flex-1 border-t border-zinc-700"></div>
                </div>

                <Button onClick={handleGoogleLogin} variant="outline" className="w-full bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700" data-testid="google-login-btn">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                </Button>

                <p className="text-center text-zinc-500 text-xs mt-4">
                  7 días de prueba gratis · Sin tarjeta
                </p>
              </>
            )}
          </div>

          {/* ===== ACCESO COMISIONISTAS ===== */}
          {authMode !== 'invitation' && (
            <div className="mt-8 bg-zinc-900/80 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 shadow-xl" data-testid="comisionista-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-zinc-800">
                  <Briefcase className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white" data-testid="comisionista-title">Acceso para Comisionistas</h3>
                  <p className="text-zinc-500 text-xs">Exclusivo para vendedores invitados por un fraccionamiento</p>
                </div>
              </div>

              <form onSubmit={handleComisionistaLogin} className="space-y-3" data-testid="comisionista-login-form">
                <div>
                  <Label className="text-zinc-400 text-sm">Correo electrónico</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="email"
                      value={comisionistaForm.email}
                      onChange={(e) => setComisionistaForm({ ...comisionistaForm, email: e.target.value })}
                      required
                      placeholder="El correo con el que fuiste invitado"
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white h-10"
                      data-testid="comisionista-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type={showComisionistaPassword ? 'text' : 'password'}
                      value={comisionistaForm.password}
                      onChange={(e) => setComisionistaForm({ ...comisionistaForm, password: e.target.value })}
                      required
                      placeholder="••••••••"
                      className="pl-10 pr-10 bg-zinc-800 border-zinc-700 text-white h-10"
                      data-testid="comisionista-password"
                    />
                    <button type="button" onClick={() => setShowComisionistaPassword(!showComisionistaPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                      {showComisionistaPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={comisionistaLoading}
                  className="w-full h-10 bg-zinc-700 hover:bg-zinc-600 text-white font-medium"
                  data-testid="comisionista-submit"
                >
                  {comisionistaLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Entrar como Comisionista'}
                </Button>
              </form>

              <div className="mt-3 flex items-center justify-between">
                <p className="text-zinc-600 text-xs">Solo acceso con invitación activa</p>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-zinc-500 hover:text-zinc-300 text-xs"
                  data-testid="comisionista-forgot-password"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 px-4 bg-zinc-950">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
            Todo lo que necesitas para gestionar tu desarrollo
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Building2, title: 'Multi-Fraccionamiento', desc: 'Gestiona múltiples proyectos desde una sola cuenta' },
              { icon: BarChart3, title: 'Dashboard Financiero', desc: 'KPIs en tiempo real de ventas, cobranza e inventario' },
              { icon: Users, title: 'Control de Equipo', desc: 'Permisos granulares para vendedores y administradores' },
              { icon: Shield, title: 'Auditoría Completa', desc: 'Historial de quién hizo qué y cuándo' },
              { icon: Star, title: 'Notificaciones', desc: 'Alertas automáticas de pagos vencidos' },
              { icon: CheckCircle, title: 'Multi-Moneda', desc: 'Opera en USD y MXN con tipo de cambio actualizado' }
            ].map((feature, i) => (
              <div key={i} className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                <feature.icon className="w-8 h-8 text-[#C6A54A] mb-4" />
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-4 bg-gradient-to-t from-[#C6A54A]/10 to-transparent">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Listo para profesionalizar tu operación?
          </h2>
          <p className="text-zinc-400 mb-8">
            Únete a los desarrolladores más exitosos de México
          </p>
          <Button
            onClick={() => document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#C6A54A] hover:bg-[#D4B85A] text-black font-bold h-12 px-8 text-lg"
          >
            Comenzar Prueba Gratis <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} Ibañez Desarrollos Inmobiliarios
          </p>
          <div className="flex items-center gap-4 text-zinc-600 text-sm">
            <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> SSL</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> GDPR</span>
          </div>
        </div>
      </footer>

      {/* Modal Recuperar Contraseña */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C6A54A]" /> Recuperar Contraseña
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 pt-4">
            <p className="text-zinc-400 text-sm">Te enviaremos un enlace para restablecer tu contraseña.</p>
            <div>
              <Label className="text-zinc-400">Correo</Label>
              <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required placeholder="tu@correo.com" className="bg-zinc-800 border-zinc-700 text-white mt-1" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)} className="flex-1 border-zinc-700 text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={sendingReset} className="flex-1 bg-[#C6A54A] text-black font-bold">
                {sendingReset ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" /> : 'Enviar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
